/**
 * #53 / DEC-360 — remembering each person's Map View layer switches (design C1).
 *
 * Browser localStorage, keyed by the signed-in user so two dispatchers sharing one PC keep
 * their own choices. Following a person across computers would need a server-side
 * preference store (a migration plus its own API); this repo has none yet, and Corbin chose
 * the browser for v1. Same pattern as the Dispatch Board's `lodex.board.showOffShiftDrivers`.
 *
 * Every read and write is guarded: storage can be blocked, full or cleared (private
 * windows, site-data settings), and the map must work the same without it — it just
 * starts with every layer off.
 */
import { DEFAULT_LAYER_PREFS, type MapLayerPrefs } from './map-layers.js';

const KEY_PREFIX = 'lodex.map.layers.';

export function layerPrefsKey(userId: string): string {
  return `${KEY_PREFIX}${userId}`;
}

/** The stored switches for `userId`, or all-off when there are none or they are unreadable. */
export function readLayerPrefs(userId: string | null, storage?: Storage): MapLayerPrefs {
  if (userId === null || userId === '') return DEFAULT_LAYER_PREFS;
  try {
    const raw = (storage ?? window.localStorage).getItem(layerPrefsKey(userId));
    if (raw === null) return DEFAULT_LAYER_PREFS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_LAYER_PREFS;
    const o = parsed as Record<string, unknown>;
    // Only an explicit `true` turns a layer on; anything else stays off.
    return {
      pickups: o['pickups'] === true,
      disposals: o['disposals'] === true,
      routes: o['routes'] === true,
    };
  } catch {
    return DEFAULT_LAYER_PREFS;
  }
}

/** Store the switches for `userId`. Failure is silent: the choice lasts for this page only. */
export function writeLayerPrefs(
  userId: string | null,
  prefs: MapLayerPrefs,
  storage?: Storage,
): void {
  if (userId === null || userId === '') return;
  try {
    (storage ?? window.localStorage).setItem(layerPrefsKey(userId), JSON.stringify(prefs));
  } catch {
    // Storage blocked or full — keep working without remembering.
  }
}
